import React, { useContext, useEffect } from 'react';
import { connect } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Header from './header/main';
import Sidebar from './sidebar/main';
import { SET_USER_AUTHENTICATED } from '../redux/actionTypes';
import { activateDemo, deactivateDemo, initializeApp, refreshUserContext } from '../redux/actions';
import store from '../../store';
import { headerHeight } from '../../constants/dimensions';
import { useLocalStorage } from '../hooks/UseLocalStorage';
import InfoModal from '../data_display/InfoModal';
import GuideInfoModal from '../data_display/GuideInfoModal';
import { useState } from 'react';
import { useAppAuth } from '../../hooks/useAppAuth';
import GuidedPilotSignupModal from '../../components/GuidedPilotSignupModal';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

const useStyles = makeStyles(theme => ({
  main: {
    paddingTop: headerHeight + 24,
    paddingBottom: 12,
    paddingLeft: 96,
    paddingRight: 24,
  },
  modalButtonContainer: {
    flex: '0 0 100%',
    marginBottom: -12,
  },
  closeModalButton: {
    width: 200,
  },
}));


function Layout(props) {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { children, isAuthenticated, demoEnabled, company, company_expired, no_companies, isNewSignup, permissionsError } = props;
  const [permissionsNoticeDismissed, setPermissionsNoticeDismissed] = useState(false);

  const [hasSeenDemoModal, setHasSeenDemoModal] = useState(sessionStorage.getItem('hasSeenDemoModal') === 'true');
  const [guidedPilotSignupModalOpen, setGuidedPilotSignupModalOpen] = useState(false);

  React.useEffect(() => {
    const startsWithDemo = location.pathname.startsWith('/demo');
    if (startsWithDemo && !hasSeenDemoModal) {
      setModalOpen(true);
    }
  }, [location, hasSeenDemoModal]);

  // Auto-open Guided Pilot modal for new signups without companies
  React.useEffect(() => {
    if (isNewSignup && isAuthenticated && !demoEnabled && no_companies) {
      // Check if user has already seen the Guided Pilot modal (in case of refresh)
      const hasSeenGuidedPilotModal = sessionStorage.getItem('hasSeenNewSignupGuidedPilotModal') === 'true';

      if (!hasSeenGuidedPilotModal) {
        // Only open if they don't have any companies
        setGuidedPilotSignupModalOpen(true);
        sessionStorage.setItem('hasSeenNewSignupGuidedPilotModal', 'true');
      }
    }
  }, [isNewSignup, isAuthenticated, demoEnabled, no_companies]);

  const [hasClearedAccountModal, setHasClearedAccountModal] = useState(false);

  React.useEffect(() => {
    const startsWithDemo = location.pathname.startsWith('/demo');
    store.dispatch(startsWithDemo ? activateDemo() : deactivateDemo());
  }, [location]);

  // Use the new auth hook instead of AWS Amplify
  // The hook handles authentication, demo mode detection, and token management
  const auth = useAppAuth();

  React.useEffect(() => {
    if (isAuthenticated || demoEnabled) {
      store.dispatch(initializeApp());
    }
  }, [isAuthenticated, demoEnabled]);

  const [modalOpen, setModalOpen] = React.useState(false);
  const handleModalClose = () => {
    sessionStorage.setItem('hasSeenDemoModal', 'true');
    setHasSeenDemoModal(true);
    setModalOpen(false);
  }

  React.useEffect(() => {
    const startsWithDemo = location.pathname.startsWith('/demo');
    if (startsWithDemo && !hasSeenDemoModal) {
      setModalOpen(true);
    }
  }, [location, hasSeenDemoModal]);

  const [accountModalOpen, setAccountModalOpen] = React.useState(false);
  const handleAccountModalClose = () => {
    setHasClearedAccountModal(true);
    setAccountModalOpen(false);
  }

  React.useEffect(() => {
    // Don't show account modal if we're showing the Guided Pilot signup modal for new users
    // For new signups with no companies, the Guided Pilot modal will handle it
    if (isNewSignup && no_companies && !company_expired) {
      setAccountModalOpen(false);
    } else {
      // Show account modal for:
      // - Expired companies (regardless of new signup status)
      // - No companies for existing users (not new signups)
      setAccountModalOpen(company_expired || (no_companies && !isNewSignup));
    }
  }, [company_expired, no_companies, isNewSignup]);

  React.useEffect(() => {
    setHasClearedAccountModal(false);
  }, [company, company_expired, no_companies]);

  const closeModalProps = {
    color: "secondary",
    variant: "contained",
    type: "submit",
    className: classes.closeModalButton,
    onClick: handleModalClose
  };

  useEffect(() => {
    if (hasSeenDemoModal && location.hash === '#reset-has-seen-demo') {
      setHasSeenDemoModal(false);
      history.push({
        ...location,
        hash: '',
      })
    }
  }, [hasSeenDemoModal, setHasSeenDemoModal, location, history]);

  // Handle successful Guided Pilot creation
  const handleGuidedPilotSuccess = async (data) => {
    console.log('Guided Pilot created successfully:', data);

    // Close the Guided Pilot signup modal
    setGuidedPilotSignupModalOpen(false);

    // Close the "no companies" modal
    setAccountModalOpen(false);

    // Load the caller's role in the new company before selecting it; otherwise the
    // new owner sees every page read-only until they log in again.
    await store.dispatch(refreshUserContext());

    // Refresh the app to load the new company data
    store.dispatch(initializeApp());

    // Redirect to the release configuration page
    history.push('/config/release');
  };

  return (
    <React.Fragment>
      <CssBaseline />
      <Header isAuthenticated={isAuthenticated} demoEnabled={demoEnabled} />

      { (isAuthenticated || demoEnabled) && <Sidebar /> }
    
      <main className={classes.main}>
        { (isAuthenticated || demoEnabled) && children }

        <GuideInfoModal title={"Welcome to our Demo Page!"} open={modalOpen} onClose={handleModalClose} closeModalProps={closeModalProps}>
          <div>
            <p>
              We're thrilled to have you here. Explore our latest version of STAR, the cutting-edge solution that's setting new standards in the industry. We've prepared four sets of project data to demonstrate the power of our Automated Defect Prediction tool across various cases, all powered by 3S. Feel free to test drive our features and read more about our tool from the menu bar.
            </p>
            <p>
              Enjoy your demo! If you have any questions or need assistance, <a href={process.env.REACT_APP_CONTACT_US_URL}>our support team</a> is just a click away.
            </p>
          </div>
        </GuideInfoModal>

        <InfoModal title="No Companies Registered" open={no_companies && accountModalOpen} onClose={handleAccountModalClose}>
          <div>
            <p>
              Looks like you don't have any companies tied to your account.
            </p>
            <p>
              <a href={`${process.env.REACT_APP_LANDING_URL}/contact`}>Contact us</a> to get started
            </p>
          </div>
        </InfoModal>

        <InfoModal title="Company Membership Expired" open={company_expired && accountModalOpen} onClose={handleAccountModalClose}>
          <div>
            <p>
              Looks like your company's membership has expired
            </p>
            <p>
              <a href={`${process.env.REACT_APP_LANDING_URL}/contact`}>Contact us</a> to continue using STAR
            </p>
          </div>
        </InfoModal>

        <Snackbar open={Boolean(permissionsError) && !permissionsNoticeDismissed} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="warning" onClose={() => setPermissionsNoticeDismissed(true)}>
            We couldn't load your permissions. Some actions may be unavailable — try reloading.
          </Alert>
        </Snackbar>

        {/* Guided Pilot Signup Modal */}
        <GuidedPilotSignupModal
          open={guidedPilotSignupModalOpen}
          onClose={() => setGuidedPilotSignupModalOpen(false)}
          onSuccess={handleGuidedPilotSuccess}
        />
      </main>
    </React.Fragment>
  );
}

function mapStateToProps(state) {
  return {
    isAuthenticated: state.common.isAuthenticated,
    demoEnabled: state.common.demoEnabled,
    company_expired: state.common.company_expired,
    no_companies: state.common.no_companies,
    company: state.common.company,
    isNewSignup: state.common.isNewSignup || false,
    permissionsError: state.common.permissionsError,
  };
} 

export default connect(mapStateToProps)(Layout)